import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Web Speech recognition shared by capture surfaces (spec 2026-11-04 §4).
// Extracted from AIExtractorDialog; adds abort-on-unmount.

const LANG: Record<string, string> = { he: "he-IL", de: "de-DE", en: "en-US" };

export const speechLang = (language?: string): string =>
  LANG[String(language || "en").split("-")[0].toLowerCase()] ?? "en-US";

/** Same separator rule AIExtractorDialog always used: one space after non-empty typed text. */
export const joinSpeech = (base: string, spoken: string): string => {
  const separator = base.length > 0 && !base.endsWith(" ") ? " " : "";
  return base + separator + spoken;
};

const recognitionCtor = (): any =>
  typeof window === "undefined"
    ? null
    : (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;

export interface SpeechInputOptions {
  /** Called with base + final + interim on every result. */
  onText?: (text: string) => void;
  /** Called for every recognition error except "aborted". */
  onError?: (error: string) => void;
}

export interface SpeechInput {
  supported: boolean;
  listening: boolean;
  /** base + final + interim of the current (or last) session. */
  text: string;
  /** The unconfirmed tail, so a host can render it muted. */
  interim: string;
  start: (baseText: string) => void;
  stop: () => void;
}

export function useSpeechInput(options: SpeechInputOptions = {}): SpeechInput {
  const { i18n } = useTranslation();
  const supported = Boolean(recognitionCtor());
  const [listening, setListening] = useState(false);
  const [text, setText] = useState("");
  const [interim, setInterim] = useState("");
  const recRef = useRef<any>(null);
  const baseRef = useRef("");
  const finalRef = useRef("");
  const callbacks = useRef(options);
  callbacks.current = options;

  const stop = useCallback(() => {
    const rec = recRef.current;
    recRef.current = null;
    if (rec) rec.stop();
    setListening(false);
    setInterim("");
  }, []);

  const start = useCallback(
    (baseText: string) => {
      const Ctor = recognitionCtor();
      if (!Ctor || recRef.current) return;
      const rec = new Ctor();
      rec.lang = speechLang(i18n?.language);
      rec.continuous = true;
      rec.interimResults = true;
      baseRef.current = baseText;
      finalRef.current = "";

      rec.onresult = (event: any) => {
        let allFinal = "";
        let tail = "";
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) allFinal += event.results[i][0].transcript;
          else tail += event.results[i][0].transcript;
        }
        finalRef.current = allFinal;
        const full = joinSpeech(baseRef.current, allFinal + tail);
        setText(full);
        setInterim(tail);
        callbacks.current.onText?.(full);
      };
      rec.onerror = (event: any) => {
        console.error("Speech recognition error:", event?.error);
        if (event?.error !== "aborted") callbacks.current.onError?.(String(event?.error ?? "unknown"));
        if (recRef.current === rec) recRef.current = null;
        setListening(false);
        setInterim("");
      };
      rec.onend = () => {
        // Commit the final transcript so a following session appends to it.
        if (finalRef.current) baseRef.current = joinSpeech(baseRef.current, finalRef.current);
        if (recRef.current === rec) recRef.current = null;
        setListening(false);
        setInterim("");
      };

      recRef.current = rec;
      rec.start();
      setListening(true);
    },
    [i18n]
  );

  useEffect(
    () => () => {
      const rec = recRef.current;
      recRef.current = null;
      if (!rec) return;
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      try {
        rec.abort();
      } catch {
        /* already stopped */
      }
    },
    []
  );

  return { supported, listening, text, interim, start, stop };
}
